import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function FeaturedProjects() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string>('');

  const { data: projects } = useQuery({
    queryKey: ['my-projects-for-featured', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('projects' as any)
        .select('id, name, city')
        .eq('developer_id', user!.id)
        .limit(50);
      return (data as any[]) || [];
    },
  });

  const { data: featured } = useQuery({
    queryKey: ['my-featured-projects', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('featured_projects' as any)
        .select('*, projects(name, city)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      return (data as any[]) || [];
    },
  });

  const { data: credits } = useQuery({
    queryKey: ['featured-project-credits', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('credit_wallet' as any)
        .select('balance').eq('user_id', user!.id).eq('credit_type', 'featured_project')
        .gt('balance', 0);
      return ((data as any[]) || []).reduce((s, r) => s + r.balance, 0);
    },
  });

  const promote = async () => {
    if (!selected) return toast.error('Pick a project first');
    const { error } = await supabase.rpc('promote_project' as any, {
      _project_id: selected, _tier: 'featured', _days: 30,
    });
    if (error) return toast.error(error.message);
    toast.success('Project promoted for 30 days');
    qc.invalidateQueries({ queryKey: ['my-featured-projects'] });
    qc.invalidateQueries({ queryKey: ['featured-project-credits'] });
  };

  const cancel = async (id: string) => {
    await supabase.from('featured_projects' as any).update({ is_active: false }).eq('id', id);
    toast.success('Promotion cancelled');
    qc.invalidateQueries({ queryKey: ['my-featured-projects'] });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Star className="h-6 w-6 text-yellow-500" /> Featured Projects
          </h1>
          <p className="text-sm text-muted-foreground">Promote your projects to the homepage hero/carousel.</p>
        </div>
        <Badge variant="outline" className="gap-1 text-sm">
          <Sparkles className="h-3 w-3 text-primary" /> {credits || 0} credits available
        </Badge>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Promote a project</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger><SelectValue placeholder="Choose a project to feature" /></SelectTrigger>
              <SelectContent>
                {projects?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} {p.city && `· ${p.city}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={promote} disabled={!selected || (credits || 0) < 1}>
            Promote (uses 1 credit, 30 days)
          </Button>
          {(credits || 0) < 1 && (
            <Button asChild variant="outline">
              <Link to="/dashboard/credit-store">Buy Featured Project credit</Link>
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Active & past promotions</CardTitle></CardHeader>
        <CardContent>
          {!featured?.length ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No promotions yet.</p>
          ) : (
            <div className="divide-y">
              {featured.map((f) => (
                <div key={f.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{f.projects?.name || '—'}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.tier} · {format(new Date(f.starts_at), 'd MMM')} → {format(new Date(f.ends_at), 'd MMM yyyy')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={f.is_active ? 'default' : 'outline'}>
                      {f.is_active ? 'Active' : 'Ended'}
                    </Badge>
                    {f.is_active && <Button size="sm" variant="outline" onClick={() => cancel(f.id)}>Cancel</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
